import React from 'react';
import { crops_overview } from './crops_overview'; // Assuming crop_overview data is imported here.
import { useParams } from 'react-router-dom';

import '../../App.css';

const CropDetail = () => {
  const { cropName } = useParams();
  const cropData = crops_overview[cropName];

  if (!cropData) {
    return <div style={styles.error}>Select a valid crop.</div>;
  }

  return (
    <div style={styles.container}>
      <div className="scrollable-content" style={styles.contentWrapper}>
        <div style={styles.headerWrapper}>
          <h2 style={styles.header}>{cropData.crop} ({cropData.scientific_name})</h2>
        </div>

        {/* Content Section */}
        <div style={styles.sectionWrapper}>
          {/* Importance in India */}
          <Section title="Importance in India">
            <p>{cropData.importance_in_india?.overview}</p>
            <strong>Key States:</strong>
            <ul style={styles.list}>
              {cropData.importance_in_india?.key_states?.map((state, index) => (
                <li key={index} style={styles.sectionContent}>{state}</li>
              ))}
            </ul>
          </Section>

          {/* Climate and Soil Requirements */}
          <Section title="Climate and Soil Requirements">
            <SubSection title="Climate">
              <Table>
                <TableRow label="Type" value={cropData.climate_and_soil_requirements?.climate?.type} />
                <TableRow label="Temperature" value={`${cropData.climate_and_soil_requirements?.climate?.temperature_min}°C - ${cropData.climate_and_soil_requirements?.climate?.temperature_max}°C`} />
                <TableRow label="Humidity" value={`${cropData.climate_and_soil_requirements?.climate?.humidity_min}% - ${cropData.climate_and_soil_requirements?.climate?.humidity_max}%`} />
              </Table>
            </SubSection>
            
            <SubSection title="Soil">
              <p>{cropData.climate_and_soil_requirements?.soil?.type}</p>
              <p>{cropData.climate_and_soil_requirements?.soil?.properties}</p>
            </SubSection>
          </Section>

          {/* Nutrient Requirements */}
          <Section title="Nutrient Requirements">
            <Table>
              <thead>
                <tr>
                  <th style={styles.th}>Nutrient</th>
                  <th style={styles.th}>Min (kg/ha)</th>
                  <th style={styles.th}>Max (kg/ha)</th>
                </tr>
              </thead>
              <tbody>
                <TableRow label="Nitrogen" valueMin={cropData.nutrient_requirements?.nitrogen_min} valueMax={cropData.nutrient_requirements?.nitrogen_max} />
                <TableRow label="Phosphorus" valueMin={cropData.nutrient_requirements?.phosphorus_min} valueMax={cropData.nutrient_requirements?.phosphorus_max} />
                <TableRow label="Potassium" valueMin={cropData.nutrient_requirements?.potassium_min} valueMax={cropData.nutrient_requirements?.potassium_max} />
              </tbody>
            </Table>
          </Section>

          {/* Varieties */}
          <Section title={`Varieties of ${cropData.crop}`}>
            <ul style={styles.list}>
              {cropData[`varieties_of_${cropName}`]?.map((variety, index) => (
                <li key={index} style={styles.sectionContent}>{variety}</li>
              ))}
            </ul>
          </Section>

          {/* Cultivation Methods */}
          <Section title="Cultivation Methods">
            <SubSection title="Sowing Method">
              <p>{cropData.cultivation_methods?.sowing_method?.description}</p>
              <p><strong>Benefits:</strong> {cropData.cultivation_methods?.sowing_method?.benefits}</p>
            </SubSection>

            <SubSection title="Irrigation">
              <p>{cropData.cultivation_methods?.irrigation?.description}</p>
              <p><strong>Method:</strong> {cropData.cultivation_methods?.irrigation?.method}</p>
            </SubSection>
          </Section>

          {/* Pest and Disease Management */}
          <Section title="Pest and Disease Management">
            <SubSection title="Common Pests">
              <ul style={styles.list}>
                {cropData.affection_and_its_control?.pests?.map((pest, index) => (
                  <li key={index} style={styles.sectionContent}>
                    <strong>{pest.name}:</strong> {pest.description}
                    <ul style={styles.nestedList}>
                      <strong>Control Measures:</strong>
                      {pest.control_measures?.map((measure, idx) => (
                        <li key={idx} style={styles.sectionContent}>{measure}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </SubSection>

            <SubSection title="Common Diseases">
              <ul style={styles.list}>
                {cropData.affection_and_its_control?.diseases?.map((disease, index) => (
                  <li key={index} style={styles.sectionContent}>
                    <strong>{disease.name}:</strong> {disease.description}
                    <ul style={styles.nestedList}>
                      <strong>Control Measures:</strong>
                      {disease.control_measures?.map((measure, idx) => (
                        <li key={idx} style={styles.sectionContent}>{measure}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </SubSection>
          </Section>

          {/* Harvesting and Post-Harvest Management */}
          <Section title="Harvesting and Post-Harvest Management">
            <p><strong>Maturity:</strong> {cropData.harvesting_and_post_harvest_management?.maturity}</p>
            <SubSection title="Harvesting Methods">
              <ul style={styles.list}>
                {cropData.harvesting_and_post_harvest_management?.harvesting_methods?.map((method, index) => (
                  <li key={index} style={styles.sectionContent}>{method}</li>
                ))}
              </ul>
            </SubSection>
            <SubSection title="Post-Harvest Processing">
              <p>{cropData.harvesting_and_post_harvest_management?.post_harvest_processing?.storage}</p>
            </SubSection>
          </Section>

          {/* Challenges Section */}
          {cropData[`challenges_in_${cropName}_cultivation`] && (
            <Section title={`Challenges in ${cropData.crop} Cultivation`}>
              <ul style={styles.list}>
                {Object.entries(cropData[`challenges_in_${cropName}_cultivation`]).map(([key, value], index) => (
                  <li key={index} style={styles.sectionContent}>
                    <strong>{formatKey(key)}:</strong> {value}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Government Initiatives Section */}
          {cropData.government_initiatives_and_policies && (
            <Section title="Government Initiatives and Policies">
              <ul style={styles.list}>
                {Object.entries(cropData.government_initiatives_and_policies).map(([key, value], index) => (
                  <li key={index} style={styles.sectionContent}>
                    <strong>{formatKey(key)}:</strong> {value}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

// Reusable Section Component
const Section = ({ title, children }) => (
  <div style={styles.card}>
    <h3 style={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);

// Reusable SubSection Component
const SubSection = ({ title, children }) => (
  <div style={styles.subSection}>
    <h4 style={styles.subSectionTitle}>{title}</h4>
    {children}
  </div>
);

// Reusable Table Component
const Table = ({ children }) => (
  <table style={styles.table}>
    {children}
  </table>
);

// Reusable TableRow Component
const TableRow = ({ label, value, valueMin, valueMax }) => (
  <tr>
    <td style={styles.td}><strong>{label}:</strong></td>
    <td style={styles.td}>{value ? value : `${valueMin} - ${valueMax}`}</td>
  </tr>
);

// Utility function to format keys to human-readable text
const formatKey = (key) => {
  return key
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
};

const styles = {
    container: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%", // Full width of the screen
      height: "100%", // Full viewport height
      display: "flex",
      justifyContent: "center", // Center horizontally
      alignItems: "center", // Center vertically
      backdropFilter: "blur(3px)", // Subtle background blur
      fontFamily: "'Poppins', sans-serif",
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'auto', // Ensure the container has scroll when needed
    },
    contentWrapper: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto', // Make the content scrollable
      width: '95%',
      height: '90%',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      border: '2px solid rgba(255, 255, 255, 0.5)',
      borderRadius: '12px'
    },
    headerWrapper: {
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      color: '#000',
      padding: '10px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.7)',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: "blur(10px)",
    },
    header: {
      fontSize: '22px',
      fontWeight: '600',
      textAlign: 'center',
      margin: '0',
    },
    sectionWrapper: {
      paddingTop: '15px', // Add some spacing from header
      paddingBottom: '20px',
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#0077cc',
      marginBottom: '5px',
    },
    subSectionTitle: {
      fontSize: '16px',
      fontWeight: '500',
      color: '#004d99',
    },
    sectionContent: {
      fontSize: '14px',
      marginBottom: '5px',
      color: '#555',
    },
    card: {
      margin: '10px',
      backgroundColor: '#fff',
      padding: '10px',
      marginBottom: '15px',
      borderRadius: '10px',
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    },
    subSection: {
      marginBottom: '10px',
    },
    list: {
      listStyleType: 'none',
      paddingLeft: '0',
    },
    nestedList: {
      paddingLeft: '10px',
      fontSize: '12px',
      color: '#666',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '5px',
    },
    td: {
      padding: '5px 10px',
      textAlign: 'left',
      borderBottom: '1px solid #ddd',
      fontSize: '12px',
      verticalAlign: 'middle',
    },
    th: {
      padding: '5px 10px',
      backgroundColor: '#4CAF50',
      color: '#fff',
      textAlign: 'left',
      fontWeight: 'bold',
    },
    error: {
      color: 'red',
      fontWeight: 'bold',
      textAlign: 'center',
      marginTop: '20px',
    },

  };
  

export default CropDetail;
